import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { type OutageBanner, useOutageBannersStore } from '@/stores/outage-banners';
import { logger } from '@/utils/logger';

/**
 * Composable for accessing outage banners with route-aware filtering.
 *
 * This composable wraps the outage banners store and adds route-based filtering
 * for the `visibleBanners` computed property. It must be called from a component
 * setup context where `useRoute()` is available.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useOutageBanners } from '@/composables/useOutageBanners';
 *
 * const { visibleBanners, closeBanner } = useOutageBanners();
 * </script>
 * ```
 */
export function useOutageBanners() {
  const store = useOutageBannersStore();
  const route = useRoute();

  /**
   * Banners that should be visible on the current route.
   * Filters out disabled banners, closed banners, and banners whose
   * url_regex doesn't match the current route path.
   */
  const visibleBanners = computed<OutageBanner[]>(() => {
    const currentPath = route.path;

    return store.banners.filter((banner) => {
      if (!banner.enabled || banner.closed) {
        return false;
      }

      if (banner.url_regex) {
        try {
          const regex = new RegExp(banner.url_regex);
          return regex.test(currentPath);
        } catch {
          logger.error(`Invalid url_regex for banner ${banner.id}`);
        }
      }

      return true;
    });
  });

  return {
    // From store
    banners: store.banners,
    closeBanner: store.closeBanner,
    updateBanners: store.updateBanners,
    loadBannersFromCDN: store.loadBannersFromCDN,
    // Route-aware computed
    visibleBanners,
  };
}
